import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { ChevronDown } from 'react-feather'
import { useReactiveVar } from '@apollo/client'
import { startOfYear } from 'date-fns'
import { EnergyType, PrimaryEnergyFlow } from '../../calculators/EnergyFlow'
import { TimeChart } from './charts/TimeChart'
import { PowerDurationChart } from './charts/PowerDurationChart'
import { TemperatureChart } from './charts/TemperatureChart'
import { useChartUtil } from './charts/use-chart-util'
import { Provider } from '../../calculators/providers/common/Provider'
import { Section } from '../common/Section'
import { BatteryChart } from './charts/BatteryChart'
import { isBatteryProvider } from '../../calculators/providers/BatteryProvider'
import { projectVar } from '../../apollo/reactive-vars'

type Props = {
  title: string
  chartTitlePrefix: string
  demands?: PrimaryEnergyFlow[]
  providers?: Provider[]
  energyTypes: EnergyType[]
}

const useStyles = makeStyles((theme) => ({
  accordionSummary: {
    padding: theme.spacing(3),
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0),
  },
}))

export function SystemContainerCharts({
  title,
  chartTitlePrefix,
  demands,
  providers,
  energyTypes,
}: Props): React.ReactElement {
  const chartUtil = useChartUtil(energyTypes, demands, providers)
  const classes = useStyles()
  const project = useReactiveVar(projectVar)

  const startOfYearDate = startOfYear(
    project?.calendarYear ? new Date(project.calendarYear, 0) : new Date(),
  )
  const batteryProviders = providers?.filter(isBatteryProvider)
  const showBatteryChart =
    energyTypes.includes(EnergyType.Electricity) &&
    batteryProviders &&
    batteryProviders.length > 0

  return (
    <Section>
      <Accordion TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary
          className={classes.accordionSummary}
          expandIcon={<ChevronDown />}
        >
          <Typography variant="h5">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Divider />
          <TimeChart
            titlePrefix={chartTitlePrefix}
            chartUtil={chartUtil}
            startOfYearDate={startOfYearDate}
          />
          <Divider />
          <PowerDurationChart
            titlePrefix={chartTitlePrefix}
            chartUtil={chartUtil}
          />
          <Divider />
          <TemperatureChart
            titlePrefix={chartTitlePrefix}
            chartUtil={chartUtil}
          />
          {showBatteryChart && (
            <>
              <Divider />
              <BatteryChart
                providers={batteryProviders}
                startOfYearDate={startOfYearDate}
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Section>
  )
}
